import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS, DONUT_COLORS, DONUT_OTHER_COLOR, donutColor } from '../typings/chart';
import { ChartPointModel }                                           from '../typings/intelligence.chart-point';
import { ChartSeriesModel }                                          from '../typings/intelligence.chart-series';
import { DonutSegmentModel }                                         from '../typings/intelligence.donut-segment';
import { IntelligenceFormatService }                                 from '../intelligence.format.service';
import { IntelligenceLowStockModel }                                 from '../typings/intelligence.low-stock';
import { IntelligenceProductFiltersModel }                           from '../typings/intelligence.product-filters';
import { IntelligenceProductsModel }                                 from '../typings/intelligence.products';
import { IntelligenceService }                                       from '../intelligence.service';
import { IntelligenceTopProductModel }                               from '../typings/intelligence.top-product';
import { TableSortModel }                                            from '../typings/intelligence.table-sort';

@Component({
  selector   : 'wor-intelligence-products-tab',
  template: require('./intelligence.products-tab.component.html')
})
export class ProductsTabComponent implements OnInit, OnDestroy {
  public error   : boolean = false;
  public loading : boolean = true;

  public filterOptions  : { brands : Array<string>; product_groups : Array<string>; product_types : Array<string>; suppliers : Array<string> } = null;
  public filters        : IntelligenceProductFiltersModel = { brand: '', product_group: '', product_type: '', supplier: '' };
  public incomeSegments : Array<DonutSegmentModel> = [];
  public lowStock       : Array<IntelligenceLowStockModel> = [];
  public lowStockSort   : TableSortModel = { direction: 'desc', field: '' };
  public soldPoints     : Array<ChartPointModel> = [];
  public sort           : TableSortModel = { direction: 'desc', field: 'revenue_ex_tax' };
  public topProducts    : Array<IntelligenceTopProductModel> = [];
  public topProductTotals = { cost_ex_tax: 0, profit_ex_tax: 0, quantity: 0, revenue_ex_tax: 0 };

  public soldSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'quantity', label: this.translateService.instant('INTELLIGENCE_SPACE.PRODUCTS.QUANTITY_SOLD') }
  ];

  private loadGeneration : number = 0;
  private productTypes   : Array<{ code : string; label : string }> = [];
  private subscription   : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  clearFilters () : void {
    this.filters = { brand: '', product_group: '', product_type: '', supplier: '' };

    this.load();
  }

  hasActiveFilters () : boolean {
    return Object.keys(this.filters).some(key => !!this.filters[ key ]);
  }

  count ( value : number ) : string {
    return this.intelligenceFormatService.count(value);
  }

  isIncomeEmpty () : boolean {
    return !this.incomeSegments.some(segment => segment.value > 0);
  }

  isLowStockEmpty () : boolean {
    return !this.lowStock.length;
  }

  isSoldEmpty () : boolean {
    return !this.soldPoints.some(point => point.values.quantity > 0);
  }

  isTopProductsEmpty () : boolean {
    return !this.topProducts.length;
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getProducts(this.filters)
    .then(( data : IntelligenceProductsModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      // Guarded so a frontend that reaches an environment before the backend does
      // keeps its last known lists rather than blanking every dropdown.
      if ( data.filter_options ) {
        // The tab shows translated type labels but filters on the raw code, so the
        // codes the period sold are kept beside the labels built from them.
        this.productTypes = ( data.filter_options.product_types || [] ).map(code => ({
          code,
          label: this.translateService.instant(`INTELLIGENCE_SPACE.PRODUCTS.TYPE_${ code.toUpperCase() }`)
        }));

        this.filterOptions = {
          brands        : data.filter_options.brands || [],
          product_groups: data.filter_options.product_groups || [],
          product_types : this.productTypes.map(type => type.label),
          suppliers     : data.filter_options.suppliers || []
        };

        if ( this.dropFiltersMissingFromOptions() ) {
          this.load();

          return;
        }
      }

      this.soldPoints = ( data.products_sold || [] ).map(row => ({
        label : this.translateService.instant(`INTELLIGENCE_SPACE.PRODUCTS.TYPE_${ row.product_type.toUpperCase() }`),
        values: { quantity: row.quantity }
      }));

      const income = data.product_income || [];
      const kept = income.slice(0, DONUT_COLORS.length);
      const remainder = income.slice(DONUT_COLORS.length).reduce(( sum, row ) => sum + row.revenue_ex_tax, 0);

      this.incomeSegments = kept.map(( row, index ) => ({
        color    : donutColor(row.label, this.filterOptions.product_groups, index),
        drillable: true,
        key      : row.label,
        label    : row.label,
        value    : row.revenue_ex_tax
      }));

      if ( remainder > 0 ) {
        this.incomeSegments.push({
          color : DONUT_OTHER_COLOR,
          label : this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
          others: income.slice(DONUT_COLORS.length).map(( row, index ) => ({
            color    : donutColor(row.label, this.filterOptions.product_groups, DONUT_COLORS.length + index),
            drillable: true,
            key      : row.label,
            label    : row.label,
            value    : row.revenue_ex_tax
          })),
          value : remainder
        });
      }

      this.topProducts = this.intelligenceFormatService.sortRows(data.top_products || [], this.sort.field, this.sort.direction);
      this.topProductTotals = this.topProducts.reduce(( totals, product ) => ({
        cost_ex_tax   : totals.cost_ex_tax + product.cost_ex_tax,
        profit_ex_tax : totals.profit_ex_tax + product.profit_ex_tax,
        quantity      : totals.quantity + product.quantity,
        revenue_ex_tax: totals.revenue_ex_tax + product.revenue_ex_tax
      }), { cost_ex_tax: 0, profit_ex_tax: 0, quantity: 0, revenue_ex_tax: 0 });

      this.lowStock = data.low_stock || [];
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  lowStockCsvRows () : Array<any> {
    return this.lowStock.map(product => ({
      item_code       : product.item_code,
      product_group   : product.product_group,
      quantity_on_hand: product.quantity_on_hand,
      minimum         : product.minimum
    }));
  }

  money ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  setFilter ( field : keyof IntelligenceProductFiltersModel, value : string ) : void {
    this.filters[ field ] = value;

    this.load();
  }

  // The type filter shows translated labels but the API takes the raw type code, so
  // these translate between the two on the way through.
  setType ( label : string ) : void {
    const match = this.productTypes.find(type => type.label === label);

    this.setFilter('product_type', match
      ? match.code
      : '');
  }

  toggleFilter ( field : keyof IntelligenceProductFiltersModel, value : string ) : void {
    this.setFilter(field, this.filters[ field ] === value
      ? ''
      : value);
  }

  toggleType ( label : string ) : void {
    this.setType(this.typeLabel() === label
      ? ''
      : label);
  }

  typeLabel () : string {
    const match = this.productTypes.find(type => type.code === this.filters.product_type);

    return match
      ? match.label
      : '';
  }

  topProductsCsvRows () : Array<any> {
    return this.topProducts.map(product => ({
      item_code     : product.item_code,
      quantity      : product.quantity,
      revenue_ex_tax: product.revenue_ex_tax,
      cost_ex_tax   : product.cost_ex_tax,
      profit_ex_tax : product.profit_ex_tax
    }));
  }

  sortBy ( field : string ) : void {
    this.sort = {
      direction: this.sort.field === field && this.sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.topProducts = this.intelligenceFormatService.sortRows(this.topProducts, this.sort.field, this.sort.direction);
  }

  sortLowStockBy ( field : string ) : void {
    this.lowStockSort = {
      direction: this.lowStockSort.field === field && this.lowStockSort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.lowStock = this.intelligenceFormatService.sortRows(this.lowStock, this.lowStockSort.field, this.lowStockSort.direction);
  }

  // product_type is held as the raw code, so it is checked against the codes rather
  // than the translated labels the dropdown shows. Reports whether anything was
  // dropped: the payload that carried these options was narrowed by a filter this
  // period has no option for, so the caller has to ask again before showing it.
  private dropFiltersMissingFromOptions () : boolean {
    const kept = {
      brand        : this.intelligenceFormatService.keepIfOffered(this.filters.brand, this.filterOptions.brands),
      product_group: this.intelligenceFormatService.keepIfOffered(this.filters.product_group, this.filterOptions.product_groups),
      product_type : this.intelligenceFormatService.keepIfOffered(this.filters.product_type, this.productTypes.map(type => type.code)),
      supplier     : this.intelligenceFormatService.keepIfOffered(this.filters.supplier, this.filterOptions.suppliers)
    };
    const dropped = Object.keys(kept).some(key => kept[ key ] !== this.filters[ key ]);

    this.filters = { ...this.filters, ...kept };

    return dropped;
  }
}
